import React, { useState, useEffect, useReducer, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';

import { Divider, Alert } from 'antd';
import { ApplicationUIContext, Form } from '@yuhuinc/ui-library';

import SelectLeadApplicant from './SelectLeadApplicant';
import SelectAdditionalApplicantsForm from './SelectAdditionalApplicantsForm';
import AssignUnitForm from './AssignUnitForm';
import AssignPackage from './AssignPackage';
import AdditionalInformation from '../additional_information/AdditionalInformation';

import { getSitesAndProperties, getApplicationPackages } from '../store/actions';
import { getTranslatedString } from 'helpers/i18n';
import { SpaceFullWidth, StyledAlert } from './Styles';
import ApplicantPendingApplicationsAlert from './ApplicantPendingApplicationsAlert';

const initialState = {
  applicantSiteAndProperties: [],
  unitSitesAndProperties: [],
  loadingSiteAndProperties: false,
  applicationPackages: [],
};

function reducer(state, [action, payload]) {
  switch (action) {
    case 'UPDATE':
      return { ...state, ...payload };
    default:
      return state;
  }
}

const BaseApplicationForm = ({
  form,
  formInitialValues,
  onSubmit,
  onError,
  disableRentForm,
  mode,
  createApplicationFailed,
}) => {
  const { isMobile } = useContext(ApplicationUIContext);
  const [
    {
      applicantSiteAndProperties,
      unitSitesAndProperties,
      loadingSiteAndProperties,
      applicationPackages,
    },
    dispatch,
  ] = useReducer(reducer, initialState);
  const [formError, setFormError] = useState(null);
  const selectedApplicant = Form.useWatch('selectedApplicant', form);
  const selectedUnit = Form.useWatch('selectedUnit', form);

  const loadSiteAndProperties = useCallback(() => {
    dispatch(['UPDATE', { loadingSiteAndProperties: true }]);
    getSitesAndProperties()
      .done((response) => {
        dispatch(['UPDATE', { loadingSiteAndProperties: false }]);
        dispatch(['UPDATE', { applicantSiteAndProperties: response.sites }]);
      })
      .catch((error) => {
        dispatch(['UPDATE', { loadingSiteAndProperties: false }]);
        onError(error);
      });

    getSitesAndProperties({ onlyReviewableProperties: true })
      .done((response) => {
        dispatch(['UPDATE', { loadingSiteAndProperties: false }]);
        dispatch(['UPDATE', { unitSitesAndProperties: response.sites }]);
      })
      .catch((error) => {
        dispatch(['UPDATE', { loadingSiteAndProperties: false }]);
        onError(error);
      });
  }, [onError]);

  const loadPackages = useCallback(
    (unitId) => {
      getApplicationPackages(unitId)
        .done((response) => {
          dispatch(['UPDATE', { applicationPackages: response.application_packages }]);
          form.setFieldsValue({ application_package_id: response.application_packages[0]?.id });
        })
        .catch((error) => {
          onError(error);
        });
    },
    [onError]
  );

  function onFinish(data) {
    setFormError(null);
    onSubmit(data);
  }

  function onFinishFailed({ errorFields }) {
    if (errorFields.length > 0) {
      setFormError({
        message: getTranslatedString('errorsOnThisPage'),
      });
    }
  }

  useEffect(() => {
    if (selectedUnit?.id) {
      loadPackages(selectedUnit.id);
    }
  }, [selectedUnit]);

  useEffect(() => {
    loadSiteAndProperties();
  }, [loadSiteAndProperties]);

  const errorsAndWarnings = [
    createApplicationFailed && (
      <Alert
        key="form_submission_error"
        message={getTranslatedString(
          'landlordPortal.applicationManagement.inviteLeadToApply.sendInvitationsFailed'
        )}
        type="error"
        showIcon
      />
    ),

    formError && (
      <StyledAlert
        key="form_validation_error"
        message={formError.message}
        type="error"
        showIcon
        isMobile={isMobile}
      />
    ),
    selectedApplicant && selectedApplicant.pending_applications?.length > 0 && (
      <ApplicantPendingApplicationsAlert
        key="applicant_with_pending_applications"
        selectedApplicant={selectedApplicant}
      />
    ),
  ].filter(Boolean);

  return (
    <Form
      form={form}
      layout="vertical"
      size={isMobile && 'large'}
      initialValues={formInitialValues}
      scrollToFirstError
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
    >
      {errorsAndWarnings.length > 0 && (
        <SpaceFullWidth direction="vertical">{errorsAndWarnings}</SpaceFullWidth>
      )}
      <SelectLeadApplicant
        selectedApplicant={selectedApplicant}
        siteAndProperties={applicantSiteAndProperties}
        loadingSiteAndProperties={loadingSiteAndProperties}
        mode={mode}
      />

      <Divider />

      {selectedApplicant && (
        <>
          <SelectAdditionalApplicantsForm
            siteAndProperties={applicantSiteAndProperties}
            loadingSiteAndProperties={loadingSiteAndProperties}
          />

          <Divider />
        </>
      )}

      <AssignUnitForm
        selectedApplicant={selectedApplicant}
        selectedUnit={selectedUnit}
        siteAndProperties={unitSitesAndProperties}
        loadingSiteAndProperties={loadingSiteAndProperties}
      />

      {selectedUnit && (
        <>
          <Divider />
          <AssignPackage applicationPackages={applicationPackages} />
        </>
      )}

      {selectedApplicant && selectedUnit && (
        <>
          <Divider />
          <AdditionalInformation
            key={selectedUnit.key}
            disableRentForm={disableRentForm}
            selectedUnit={selectedUnit}
            selectedApplicant={selectedApplicant}
            mode={mode}
          />
        </>
      )}
    </Form>
  );
};

BaseApplicationForm.propTypes = {
  form: PropTypes.object.isRequired,
  formInitialValues: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onError: PropTypes.func,
  disableRentForm: PropTypes.bool,
  mode: PropTypes.oneOf(['create', 'edit']),
};

BaseApplicationForm.defaultProps = {
  onError: () => {},
  disableRentForm: false,
};

export default BaseApplicationForm;
